import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import {
    AcquiringType,
    SalesChannels,
    CurrencyCode,
    CreditCardPaymentDetailsFormValues,
} from "../supplierMerchantsTypes"
import {
    FetchAcquirerAgreementsResponse,
    FetchBankAccountsResponse,
    FetchIndustryCodesResultResponse,
    fetchAcquirerAgreementsForSupplier,
    fetchBankAccountsResult,
    fetchIndustryCodesResult,
    POSQuery,
} from "../../../settings/posAcquirer/SupplierAdminPOSApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { FormikDropdown, FormikMultipleAutoComplete } from "../../../../../../shared/components/forms/Formik"
import { WellknownAgreementType } from "../../../settings/posAcquirer/SupplierAdminPOSTypes"
import { SUPPLIER_ADMIN_ACQUIRING_ROUTE } from "../../../../../../constants/routes"
import { Form, FormikHelpers, useFormikContext } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import { Dispatch, SetStateAction, useCallback, useEffect } from "react"
import { makeStyles } from "tss-react/mui"
import { DialogContent, Grid, Link, MenuItem, Typography } from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"

const useStyles = makeStyles()((theme) => ({
    dialogTopbar: {
        padding: "0.5rem 0.5rem 0",
    },
    dialogContent: {
        padding: "2rem 4rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
    },
    dialogBox: {
        gap: "2.5rem",
    },
    dialogBoxTitle: {
        textAlign: "center",
    },
    listWrap: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    listTitle: {
        color: theme.palette.tailwind.slate[500],
        background: theme.palette.tailwind.slate[50],
        padding: "0.75rem 1.25rem",
    },
    infoWrap: {
        display: "grid",
        gridTemplateColumns: "14px 1fr",
        background: theme.palette.tailwind.sky[50],
        borderRadius: "0.5rem",
        gap: "10px",
        padding: "0.75rem",
        color: theme.palette.tailwind.sky[700],
    },
    button: {
        margin: "2rem auto 0",
        padding: "1rem 2rem",
    },
}))

type CreditCardPaymentsDialogProps = Readonly<{
    dialogState: boolean
    setDialogState: Dispatch<SetStateAction<boolean>>
    isBankAccount: boolean
    isPaymentSetup: boolean
    initialValues?: CreditCardPaymentDetailsFormValues
    handleSavePayment: (data: CreditCardPaymentDetailsFormValues) => void
}>

export const CreditCardPaymentsDialog = ({
    dialogState,
    setDialogState,
    isBankAccount,
    isPaymentSetup,
    initialValues,
    handleSavePayment,
}: CreditCardPaymentsDialogProps) => {
    const handleClose = useCallback(() => {
        setDialogState((prev) => !prev)
    }, [setDialogState])

    const defaultInitialValues = {
        type: "",
        agreement: {
            id: "",
        },
        salesChannels: SalesChannels.POS,
        defaultCurrencyCode: CurrencyCode.DKK,
        bankAccount: {
            refId: "",
        },
        industryCodes: [],
        bankAccountInfo: {},
    }

    function handleSubmit(
        values: CreditCardPaymentDetailsFormValues,
        _helpers: FormikHelpers<CreditCardPaymentDetailsFormValues>,
    ) {
        {
            handleSavePayment(values)
            handleClose()
        }
    }

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id="create-or-edit-supplier-admin-merchant-dialog-title">
                    <span>Credit card payment setup</span>
                </LocalizedStrict>
            }
            open={dialogState}
            onClose={handleClose}
            maxWidthProp="sm"
            scroll="body"
        >
            <Formik<CreditCardPaymentDetailsFormValues>
                enableReinitialize
                initialValues={initialValues || defaultInitialValues}
                onSubmit={handleSubmit}
            >
                <Form>
                    <CreditCardPaymentFormContent isBankAccount={isBankAccount} isPaymentSetup={isPaymentSetup} />
                </Form>
            </Formik>
        </StandardDialog>
    )
}

type CreditCardPaymentFormContentProps = Readonly<{
    isBankAccount: boolean
    isPaymentSetup: boolean
}>

const selectIndustryCodes = (data: FetchIndustryCodesResultResponse) => data?.industryCodes
const selectBankAccounts = (data: FetchBankAccountsResponse) => data?.bankAccounts
const selectSupplierAgreements = (data: FetchAcquirerAgreementsResponse) => data?.agreements || []

export function CreditCardPaymentFormContent({ isBankAccount, isPaymentSetup }: CreditCardPaymentFormContentProps) {
    const { classes } = useStyles()
    const { supplierId } = useCurrentSupplier()
    const { values, setFieldValue } = useFormikContext<CreditCardPaymentDetailsFormValues>()

    const { data: supplierAgreementsData } = useApiQuery(fetchAcquirerAgreementsForSupplier, {
        queryName: POSQuery.AGREEMENTS_DETAILS,
        dependencies: {
            supplierId,
        },
        select: selectSupplierAgreements,
    })

    const industryCodesResult = useApiQuery(fetchIndustryCodesResult, {
        queryName: POSQuery.INDUSTRY_CODES_DETAILS,
        dependencies: {
            supplierId,
        },
        select: selectIndustryCodes,
    })
    const industryCodesData = industryCodesResult.data

    const bankAccountsResult = useApiQuery(fetchBankAccountsResult, {
        queryName: POSQuery.BANK_ACCOUNT_CODES_DETAILS,
        dependencies: {
            supplierId,
        },
        select: selectBankAccounts,
    })
    const bankAccountsData = bankAccountsResult.data

    useEffect(() => {
        const codesList =
            industryCodesData &&
            industryCodesData.filter((obj) => values?.industryCodes.includes(obj.refId)).map((obj) => obj.code)
        setFieldValue("bankAccountInfo.industryCodes", codesList || [])
    }, [values?.industryCodes])

    return (
        <DialogContent className={classes.dialogContent}>
            <Grid container direction="column" className={classes.dialogBox}>
                <LocalizedStrict
                    id="create-or-edit-supplier-admin-merchant-dialog-field-acquiring-agreement"
                    attrs={{ label: true }}
                >
                    <FormikDropdown
                        name="agreement.id"
                        label="Acquiring agreement"
                        defaultValue={
                            isBankAccount
                                ? supplierAgreementsData?.find((el) => el.type === WellknownAgreementType.ADYEN_POS)?.id
                                : undefined
                        }
                        fullWidth
                        onChange={(e) => {
                            const checkType = supplierAgreementsData?.find((el) => el.id === e.target.value)
                            if (checkType?.type === WellknownAgreementType.ADYEN_POS) {
                                setFieldValue("type", AcquiringType.ADYEN)
                            }
                        }}
                        disabled={isPaymentSetup}
                    >
                        {supplierAgreementsData?.map((item, index) => (
                            <MenuItem value={item.id} key={index}>
                                {item?.adyenAgreement?.acquirer.displayName}
                            </MenuItem>
                        ))}
                    </FormikDropdown>
                </LocalizedStrict>
                <LocalizedStrict
                    id="create-or-edit-supplier-admin-merchant-dialog-field-sales-channels"
                    attrs={{ label: true }}
                >
                    <FormikDropdown
                        defaultValue={SalesChannels.POS}
                        name="salesChannels"
                        label="Sales channels"
                        fullWidth
                        disabled={isPaymentSetup}
                    >
                        <MenuItem value={SalesChannels.POS}>{SalesChannels.POS}</MenuItem>
                    </FormikDropdown>
                </LocalizedStrict>
                <Grid>
                    <LocalizedStrict
                        id="create-or-edit-supplier-admin-merchant-dialog-field-bank-account"
                        attrs={{ label: true }}
                    >
                        <FormikDropdown
                            name="bankAccount.refId"
                            label="Bank account"
                            fullWidth
                            onChange={(e) => {
                                const getAccountIdentifier = bankAccountsData?.find((el) => el.refId === e.target.value)

                                setFieldValue(
                                    "bankAccountInfo.accountIdentifier",
                                    getAccountIdentifier?.accountIdentifier,
                                )
                                setFieldValue("bankAccountInfo.bankName", getAccountIdentifier?.bankName)
                            }}
                            disabled={isPaymentSetup}
                        >
                            {bankAccountsData?.map((item, index) => (
                                <MenuItem value={item.refId} key={index}>
                                    {item.accountIdentifier} {item?.bankName ? `(${item?.bankName})` : ""}
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="create-or-edit-supplier-admin-merchant-dialog-field-bank-account-helper"
                        elems={{
                            retryLink: <Link component={RouterLink} to={SUPPLIER_ADMIN_ACQUIRING_ROUTE}></Link>,
                        }}
                    >
                        <Typography variant="caption">
                            Select the bank account where to sales will be transfered. You will only see banks accounts
                            which have been created in the Adyen platform. To add a new bank account see{" "}
                            <Link>guide.</Link>
                        </Typography>
                    </LocalizedStrict>
                </Grid>

                <LocalizedStrict
                    id="create-or-edit-supplier-admin-merchant-dialog-field-industry-codes"
                    attrs={{ label: true }}
                >
                    <FormikMultipleAutoComplete
                        name="industryCodes"
                        label="Industry codes"
                        fullWidth
                        options={industryCodesData || []}
                        isOptionEqualToValue={(option, value) => option.refId === value}
                        areOptionsEqual={(x, y) => x.refId === y.refId}
                        getOptionLabel={(option) => option.code}
                        getOptionValue={(option) => option.refId}
                    />
                </LocalizedStrict>
                <LocalizedStrict
                    id="create-or-edit-supplier-admin-merchant-dialog-field-default-currency"
                    attrs={{ label: true }}
                >
                    <FormikDropdown
                        name="defaultCurrencyCode"
                        label="Default currency"
                        fullWidth
                        defaultValue={CurrencyCode.DKK}
                        disabled={isPaymentSetup}
                    >
                        <MenuItem value={CurrencyCode.DKK}>{CurrencyCode.DKK}</MenuItem>
                    </FormikDropdown>
                </LocalizedStrict>
            </Grid>

            <LocalizedStrict id="create-or-edit-supplier-admin-merchant-dialog-save-button">
                <StandardButton
                    fullWidth={false}
                    type="submit"
                    className={classes.button}
                    disabled={!values?.bankAccount?.refId || !values?.type || !values?.industryCodes.length}
                >
                    Save
                </StandardButton>
            </LocalizedStrict>
        </DialogContent>
    )
}
